import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { PpjBonusResponse, SetPpjBudgetDto, SwaggerException } from '../../sdk/sdk';
import { PpjService } from '../../services/ppj.service';
import { commissionErrorMessage } from '../../utils/commission-error-message';
import { currencyAmount } from '../../utils/form-validators';
import { formatMoneyInput, parseMoneyInput, roundMoney } from '../../utils/money';
import { budgetedLaborPercent } from '../../utils/ppj-rules';

export const PPJ_BUDGET_NOT_FOUND_ERROR = 'Project not found.';
export const PPJ_BUDGET_SAVE_ERROR = 'Failed to set the bonus pot. Please try again.';
export const PPJ_BUDGET_POT_REQUIRED_ERROR = 'Enter a bonus pot amount before saving.';
export const PPJ_BUDGET_POT_INVALID_ERROR = 'Enter the bonus pot as a dollar amount.';
export const PPJ_BUDGET_LOCKED_NOTE = 'Locked — payout recorded.';
export const PPJ_BUDGET_UNKNOWN_PERCENT = '—';

@Component({
  selector: 'app-ppj-budget-form',
  imports: [ReactiveFormsModule],
  templateUrl: './ppj-budget-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PpjBudgetFormComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly ppjService = inject(PpjService);
  private readonly destroyRef = inject(DestroyRef);

  readonly projectId = input.required<string>();
  readonly bonus = input<PpjBonusResponse | null>(null);
  readonly defaultBonusPot = input<number | null>(null);
  readonly adjustedSale = input<number | null>(null);
  readonly actualLaborCost = input<number | null>(null);
  readonly locked = input<boolean>(false);
  readonly canEdit = input<boolean>(true);

  readonly saved = output<void>();

  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly lockedNote = PPJ_BUDGET_LOCKED_NOTE;

  readonly form = this.formBuilder.group({
    bonusPot: this.formBuilder.control<string | null>(null, [Validators.required, currencyAmount]),
    budgetedLaborPercent: this.formBuilder.control<string | null>(null),
  });

  private readonly enteredBonusPot = toSignal(this.form.controls.bonusPot.valueChanges, {
    initialValue: this.form.controls.bonusPot.value,
  });

  private readonly budgetedLaborDisplay = computed(() => {
    const percent = budgetedLaborPercent(
      parseMoneyInput(this.enteredBonusPot()), this.actualLaborCost(), this.adjustedSale());
    return percent === null ? PPJ_BUDGET_UNKNOWN_PERCENT : percent.toFixed(2);
  });

  constructor() {
    this.form.controls.budgetedLaborPercent.disable({ emitEvent: false });

    effect(() => {
      const pot = this.bonus()?.bonusPot ?? this.defaultBonusPot() ?? null;
      this.form.controls.bonusPot.reset(pot === null ? null : formatMoneyInput(pot));
    });

    effect(() => {
      this.form.controls.budgetedLaborPercent.setValue(
        this.budgetedLaborDisplay(), { emitEvent: false });
    });

    effect(() => {
      if (this.locked() || !this.canEdit()) {
        this.form.controls.bonusPot.disable({ emitEvent: false });
        return;
      }
      this.form.controls.bonusPot.enable({ emitEvent: false });
    });
  }

  onBonusPotBlur(): void {
    const amount = parseMoneyInput(this.form.controls.bonusPot.value);
    if (amount === null) {
      return;
    }
    this.form.controls.bonusPot.setValue(formatMoneyInput(amount));
  }

  submit(): void {
    if (this.locked() || !this.canEdit()) {
      return;
    }
    if (this.form.controls.bonusPot.invalid) {
      this.errorMessage.set(this.potErrorMessage());
      return;
    }

    this.errorMessage.set(null);
    this.saving.set(true);

    const dto = new SetPpjBudgetDto();
    const amount = parseMoneyInput(this.form.controls.bonusPot.value);
    dto.bonusPot = amount === null ? undefined : roundMoney(amount);

    this.ppjService
      .setBudget(this.projectId(), dto)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.saving.set(false);
          this.saved.emit();
        },
        error: (err: unknown) => {
          this.saving.set(false);
          this.errorMessage.set(this.extractErrorMessage(err));
        },
      });
  }

  private potErrorMessage(): string {
    return this.form.controls.bonusPot.hasError('currencyAmount')
      ? PPJ_BUDGET_POT_INVALID_ERROR
      : PPJ_BUDGET_POT_REQUIRED_ERROR;
  }

  private extractErrorMessage(err: unknown): string {
    if (SwaggerException.isSwaggerException(err)) {
      if (err.status === 404) {
        return PPJ_BUDGET_NOT_FOUND_ERROR;
      }
      if (err.status === 409) {
        return commissionErrorMessage(err);
      }
    }
    return PPJ_BUDGET_SAVE_ERROR;
  }
}
